import React, { useEffect, useId, useRef } from 'react';
import { useTranslation } from '../../i18n';
import './Modal.css';
import Icon from '../Icon';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  /** When true, Escape and backdrop click do not close */
  disableClose?: boolean;
  className?: string;
  contentClassName?: string;
  hideHeader?: boolean;
  /** Optional labelledby override when custom header is used */
  ariaLabelledBy?: string;
  /**
   * Behaviour only (UX-32): dialog semantics, Escape, focus trap and backdrop click, but no shared
   * chrome. `className` styles the backdrop and `contentClassName` the dialog, so existing modal
   * designs keep their look. Without a title, the first heading inside labels the dialog.
   */
  bare?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open dialogs, innermost last: only the top one reacts to Escape and Tab. */
const openStack: object[] = [];

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  disableClose = false,
  className = '',
  contentClassName = '',
  hideHeader = false,
  ariaLabelledBy,
  bare = false,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  // Remember the trigger while rendering the open state: an `autoFocus` field inside the dialog
  // takes focus before effects run, so an effect would record the field instead.
  if (isOpen && !wasOpen.current) {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
  }
  wasOpen.current = isOpen;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const disableCloseRef = useRef(disableClose);
  disableCloseRef.current = disableClose;

  useEffect(() => {
    if (!isOpen) return;

    const token = {};
    openStack.push(token);

    const dialog = dialogRef.current;
    if (dialog && !dialog.hasAttribute('aria-labelledby') && !dialog.hasAttribute('aria-label')) {
      const heading = dialog.querySelector<HTMLElement>('h1, h2, h3, h4');
      if (heading) {
        heading.id ||= `${titleId}-heading`;
        dialog.setAttribute('aria-labelledby', heading.id);
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (openStack[openStack.length - 1] !== token) return;
      if (event.key === 'Escape' && !disableCloseRef.current) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Focus the first form field (or control), else the dialog itself; skip if content already took focus
    const frame = requestAnimationFrame(() => {
      const root = dialogRef.current;
      if (!root || root.contains(document.activeElement)) return;
      const target =
        root.querySelector<HTMLElement>('input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])') ||
        root.querySelector<HTMLElement>(FOCUSABLE);
      (target || root).focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown);
      openStack.splice(openStack.indexOf(token), 1);
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen, titleId]);

  if (!isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && !disableClose) {
      onClose();
    }
  };

  const labelledBy = ariaLabelledBy || (title ? titleId : undefined);

  if (bare) {
    return (
      <div className={`modal-root ${className}`.trim()} onClick={handleBackdropClick}>
        <div
          ref={dialogRef}
          className={`modal-sheet ${contentClassName}`.trim()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={`modal-root shared-modal-backdrop ${className}`.trim()} onClick={handleBackdropClick}>
      <div
        ref={dialogRef}
        className={`modal-sheet shared-modal shared-modal--${size} ${contentClassName}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {!hideHeader && (
          <div className="shared-modal__header">
            {title != null && (
              <h3 id={titleId} className="shared-modal__title">
                {title}
              </h3>
            )}
            <button
              type="button"
              className="shared-modal__close"
              onClick={onClose}
              disabled={disableClose}
              aria-label={t('common.close')}
            >
              <Icon name="close" />
            </button>
          </div>
        )}
        <div className="shared-modal__body">{children}</div>
        {footer != null && <div className="shared-modal__footer">{footer}</div>}
      </div>
    </div>
  );
};

export default Modal;
